/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import store from "../../../state/store";
import Expander from "../../../components/Expander";
import PublicationLinks from "../../../components/Publications/components/PublicationLinks";
import { Table } from "../../../components/atoms";
import ChartsGroup from "../../../components/atoms/charts/ChartsGroup";
import {
  getSangerCosmicSchemaProperties,
  getSangerCosmicLicensedVersion,
  getSangerCosmicLicensed,
} from "../../../state/selectors/CosmicComponent.selectors";
import AgeChart from "../../../components/atoms/charts/AgeChart";
import Chart from "../../../components/atoms/charts/Chart";
import {
  flushOrange,
  likelyBenignColor,
  likelyPathogenicColor,
} from "../../../common/helpers/colors";
import {
  getVarsoUrl,
  renderSingleWithLabel,
  renderMultipleWithLabel,
  cropNumber,
  constructKeyValue,
} from "../../../common/helpers/helpers";
import withErrorGuard from "../../../HOC/withErrorGuard";
import GeneWithRefGenomeLink from "../../../common/helpers/GeneWithRefGenomeLink";
import ComponentPanel from "../../../components/atoms/ComponentPanel/ComponentPanel";

const PREDICTIONS_COLORS = {
  Pathogenic: likelyPathogenicColor,
  Neutral: likelyBenignColor,
};

function getSangerUrl(item) {
  let id;
  if (Object.prototype.hasOwnProperty.call(item, "id")) {
    id = item.id;
  } else {
    id = item;
  }
  return `https://cancer.sanger.ac.uk/cosmic/search?q=${id}`;
}

class SangerCosmicLicensed extends React.Component {
  constructor() {
    super();
    this.state = {};

    this.age_freq_list = [
      "<1",
      0,
      "1-5",
      0,
      "5-10",
      0,
      "10-20",
      0,
      "20-30",
      0,
      "30-40",
      0,
      "40-50",
      0,
      "50-60",
      0,
      "60-70",
      0,
      "70-80",
      0,
      "80-90",
      0,
      "90-100",
      0,
    ];
  }

  getValue = (item, labels, name) => {
    if (item[name]) {
      return renderSingleWithLabel(item, name, labels[name].label, true);
    }
    return null;
  };

  getMultipleValues = (item, labels, name) => {
    if (item[name]) {
      return renderMultipleWithLabel(item, name, labels[name].label);
    }
    return null;
  };

  getGenes = (item, labels) => {
    if (item.gene) {
      return (
        <div className="varsome-horiz-list-item">
          <div className="varsome-horiz-list-label">{labels.gene.label}</div>
          <div className="varsome-horiz-list-value">
            {item.gene.map((gene) => {
              return (
                <div key={gene}>
                  <GeneWithRefGenomeLink
                    href={`${store.getState().varsomeHost}/gene/${gene}`}
                  >
                    {gene}
                  </GeneWithRefGenomeLink>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  getDrugs = (item, labels) => {
    if (item.drug_entries) {
      const {
        drug_name,
        somatic_status,
        zygosity,
        census_gene,
        gene,
        transcript,
      } = labels.drug_entries.items.properties;
      const tableData = [];

      const tableHeaders = [
        { id: uuidv4(), title: drug_name.label },
        { id: uuidv4(), title: somatic_status.label },
        { id: uuidv4(), title: zygosity.label },
        { id: uuidv4(), title: census_gene.label },
        { id: uuidv4(), title: "Histology" },
        { id: uuidv4(), title: "Tissue" },
        { id: uuidv4(), title: gene.label },
        { id: uuidv4(), title: transcript.label },
        { id: uuidv4(), title: "Publications" },
      ];
      item.drug_entries.forEach((drugEntry) => {
        const histologyDrugChartData = constructKeyValue(
          drugEntry.histology_freq
        );
        const tissueFreqDrugData = constructKeyValue(drugEntry.tissue_freq);

        const tableRow = [
          {
            id: uuidv4(),
            columns: [
              {
                id: uuidv4(),
                content: drugEntry.drug_name || "-",
              },
              {
                id: uuidv4(),
                content: drugEntry.somatic_status || "-",
              },
              {
                id: uuidv4(),
                content: drugEntry.zygosity || "-",
              },
              {
                id: uuidv4(),
                content: drugEntry.census_gene || "-",
              },
              {
                id: uuidv4(),
                content: histologyDrugChartData.map((h) => (
                  <span>{h.key}</span>
                )),
              },
              {
                id: uuidv4(),
                content: tissueFreqDrugData.map((h) => <span>{h.key}</span>),
              },
              {
                id: uuidv4(),
                content: (
                  <GeneWithRefGenomeLink
                    href={`${store.getState().varsomeHost}/gene/${
                      drugEntry.gene
                    }`}
                  >
                    {drugEntry.gene || "-"}
                  </GeneWithRefGenomeLink>
                ),
              },
              {
                id: uuidv4(),
                content: (
                  <a
                    href={`${store.getState().varsomeHost}/transcript/${
                      store.getState().genome.name
                    }/${drugEntry.transcript}`}
                  >
                    {drugEntry.transcript || "-"}
                  </a>
                ),
              },
              {
                id: uuidv4(),
                content:
                  drugEntry.pub_med_references !== null &&
                  drugEntry.pub_med_references.length > 0 ? (
                    <PublicationLinks ids={drugEntry.pub_med_references} />
                  ) : (
                    "-"
                  ),
              },
            ],
          },
        ];
        tableData.push(...tableRow);
      });

      return (
        <div className="prisma_tables">
          <div className="table-container">
            <h5>Drug Entries</h5>
            <div className="varsome-horiz-flex">
              <Table headers={tableHeaders} rows={tableData} />
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  getItemDetails = (item, labels, title) => {
    return (
      <div className="varsome-horiz-flex">
        {this.getValue(item, labels, "entry_type")}
        {item.cosmic_id && (
          <div className="varsome-horiz-list-item">
            <div className="varsome-horiz-list-label">{title}</div>
            <div className="varsome-horiz-list-value">
              <Expander showMore="4">
                {item.cosmic_id.map((entry, i) => {
                  const id = entry.hasOwnProperty("id") ? entry.id : entry;
                  return (
                    <div key={i}>
                      <a href={getVarsoUrl(getSangerUrl(id))} target="_blank">
                        {id}
                      </a>
                      {entry.is_consistent === false && <span> †</span>}
                    </div>
                  );
                })}
              </Expander>
            </div>
          </div>
        )}
        {item.legacy_cosmic_id && (
          <div className="varsome-horiz-list-item">
            <div className="varsome-horiz-list-label">
              {labels.legacy_cosmic_id.label}
            </div>
            <div className="varsome-horiz-list-value">
              {Array.isArray(item.legacy_cosmic_id) &&
                item.legacy_cosmic_id.length > 0 &&
                item.legacy_cosmic_id.map((cosmicId, idx) => {
                  return (
                    <div key={idx}>
                      <a
                        href={getVarsoUrl(getSangerUrl(cosmicId))}
                        target="_blank"
                      >
                        {cosmicId}
                      </a>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
        {this.getMultipleValues(item, labels, "description")}
        {this.getGenes(item, labels)}
        {this.getMultipleValues(item, labels, "resistance_mutation")}
        {this.getValue(item, labels, "num_entries")}
        {this.getValue(item, labels, "num_samples")}
        {item.accession_number && (
          <div className="varsome-horiz-list-item">
            <div className="varsome-horiz-list-label">
              {labels.accession_number.label}
            </div>
            <div className="varsome-horiz-list-value">
              {item.accession_number.map((acc, i) => {
                return (
                  <div key={i}>
                    <a
                      href={getVarsoUrl(
                        `https://www.ncbi.nlm.nih.gov/CCDS/CcdsBrowse.cgi?REQUEST=NUCID&DATA=${acc}&BUILDS=ALLBUILDS`
                      )}
                      target="_blank"
                    >
                      {acc}
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {item.fathmm_score && (
          <div className="varsome-horiz-list-item">
            <div className="varsome-horiz-list-label">FATHMM score</div>
            <div
              className="varsome-horiz-list-value"
              style={
                // eslint-disable-next-line no-nested-ternary
                item.fathmm_prediction
                  ? PREDICTIONS_COLORS[item.fathmm_prediction]
                    ? {
                        color: PREDICTIONS_COLORS[item.fathmm_prediction],
                      }
                    : ""
                  : { color: flushOrange }
              }
            >
              {cropNumber(item.fathmm_score)}
            </div>
          </div>
        )}
        {item.fathmm_prediction && (
          <div className="varsome-horiz-list-item">
            <div className="varsome-horiz-list-label">FATHMM Prediction</div>
            <div
              className="varsome-horiz-list-value"
              style={
                PREDICTIONS_COLORS[item.fathmm_prediction]
                  ? {
                      color: PREDICTIONS_COLORS[item.fathmm_prediction],
                    }
                  : ""
              }
            >
              {item.fathmm_prediction}
            </div>
          </div>
        )}
        {this.getValue(item, labels, "fathmm_mkl_coding_score")}
        {this.getValue(item, labels, "fathmm_mkl_coding_groups")}
        {this.getValue(item, labels, "fathmm_mkl_non_coding_score")}
        {this.getValue(item, labels, "fathmm_mkl_non_coding_groups")}
      </div>
    );
  };

  getCharts = (item) => {
    const histologyChartData = constructKeyValue(item.histology_freq);
    const genomeWideScreenFreqData = constructKeyValue(
      item.genome_wide_screen_freq
    );
    const lohFreqData = constructKeyValue(item.loh_freq);
    const ageFreqData = constructKeyValue(item.age_freq, false);
    const zygosityFreqData = constructKeyValue(item.zygosity_freq);
    const tumorOriginFreqData = constructKeyValue(item.tumour_origin_freq);
    const somaticStatusFreqData = constructKeyValue(item.somatic_status_freq);
    const primarySiteFreqData = constructKeyValue(item.primary_site_freq);
    const wholeExomeFreqData = constructKeyValue(item.whole_exome_freq);
    const wholeGenomeReseqFreqData = constructKeyValue(
      item.whole_genome_reseq_freq
    );

    // If there's an age frequency, include all the values in the age freq list
    if (item.age_freq) {
      const tmp = this.age_freq_list.slice();
      for (let i = 0; i <= tmp.length - 1; i += 2) {
        const idx = item.age_freq.indexOf(tmp[i]);
        if (idx !== -1) {
          tmp[i + 1] = item.age_freq[idx + 1];
        }
      }
      item.age_freq = tmp;
    }
    return (
      <ChartsGroup>
        <Chart
          data={histologyChartData}
          title="Histology frequency"
          showEmptyState={false}
        />
        <Chart
          data={genomeWideScreenFreqData}
          title="Genome wide screen frequency"
          showEmptyState={false}
        />
        <Chart
          data={lohFreqData}
          title="LOH frequency"
          showEmptyState={false}
        />
        <AgeChart
          data={ageFreqData}
          title="Age frequency"
          showEmptyState={false}
        />
        <Chart
          data={zygosityFreqData}
          title="Zygosity frequency"
          showEmptyState={false}
        />
        <Chart
          data={tumorOriginFreqData}
          title="Tumor origin frequency"
          showEmptyState={false}
        />
        <Chart
          data={somaticStatusFreqData}
          title="Somatic status frequency"
          showEmptyState={false}
        />
        <Chart
          data={primarySiteFreqData}
          title="Primary site"
          showEmptyState={false}
        />
        <Chart
          data={wholeExomeFreqData}
          title="Whole exome frequency"
          showEmptyState={false}
        />
        <Chart
          data={wholeGenomeReseqFreqData}
          title="Whole genome reseq frequency"
          showEmptyState={false}
        />
      </ChartsGroup>
    );
  };

  render() {
    const { cosmicLicensed, cosmicSchemaProperties, cosmicLicensedVersion } =
      this.props;

    if (!cosmicLicensed || !cosmicSchemaProperties) {
      return null;
    }

    const labels =
      cosmicSchemaProperties.items.properties.items.items.properties;
    const values = cosmicLicensed;

    const content = values.map((value) => {
      return value.items.map((item, i) => {
        let hasIncosistent;
        if (
          Array.isArray(item.cosmic_id) &&
          item.cosmic_id.length > 0 &&
          item.cosmic_id[0].hasOwnProperty("id")
        ) {
          hasIncosistent = item.cosmic_id.some((id) => {
            return !id.is_consistent;
          });
        }

        let title;
        if (item.legacy_cosmic_id) {
          title = "Genomic Mutation ID";
        } else {
          title = labels.cosmic_id.label;
        }
        return (
          <div className="varsome-inner-container" key={i}>
            {i > 0 && <hr />}
            {this.getItemDetails(item, labels, title)}
            {hasIncosistent && (
              <div
                className="varsome-text-muted tw-my-4 tw-mx-0"
                style={{ fontSize: "0.8rem" }}
              >
                † : Variant with inconsistent representations in COSMIC. Some
                such variants have incorrect genomic and/or transcript
                coordinates in COSMIC and therefore may not be connected to the
                correct variant on this site.
              </div>
            )}
            {this.getCharts(item)}
            {this.getDrugs(item, labels)}
            {item.pub_med_references && (
              <div className="varsome-horiz-flex">
                <div className="varsome-horiz-list-item">
                  <div className="varsome-horiz-list-label">Publications</div>
                  <div className="varsome-horiz-list-value">
                    {item.pub_med_references.length > 0 ? (
                      <PublicationLinks ids={item.pub_med_references} />
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      });
    });

    return (
      <div className="varsome-result-fixed">
        <div className="varsome-container varsome-container__basic">
          <ComponentPanel
            version={cosmicLicensedVersion}
            url={getVarsoUrl("http://cancer.sanger.ac.uk/cosmic")}
            content={content}
            title="COSMIC"
          />
        </div>
      </div>
    );
  }
}

SangerCosmicLicensed.propTypes = {
  cosmicLicensedVersion: PropTypes.string,
  cosmicSchemaProperties: PropTypes.shape({}),
};

SangerCosmicLicensed.defaultProps = {
  cosmicLicensedVersion: null,
  cosmicSchemaProperties: {},
};

const mapStateToProps = (state) => {
  return {
    cosmicSchemaProperties: getSangerCosmicSchemaProperties(state),
    cosmicLicensedVersion: getSangerCosmicLicensedVersion(state),
    cosmicLicensed: getSangerCosmicLicensed(state),
  };
};

export default connect(
  mapStateToProps,
  {}
)(withErrorGuard(SangerCosmicLicensed, "Sanger Cosmic Licensed", "COSMIC"));
